import React from "react";
import classNames from "classnames";
import ClipLoader from 'react-spinners/ClipLoader';
import Framework from "@beardeddevops/react.framework";
import API from "@beardeddevops/react.api";
import Types from "constants/Types";
import {Button, ButtonGroup} from "reactstrap";
import PropTypes from "prop-types";
import TextField from "@material-ui/core/TextField/index";
import moment from "moment";
import {
	appendCashLeftDenomMismatchNote,
	buildDenomPayload,
	computeCounted,
	DENOMINATIONS,
	emptyDenomCounts,
	formatMoney,
	getCashLeftDenomMismatch,
	parseCustomizationBool,
	parseDenominations,
} from "utils/registerDenominations";
import {
	resolveCashLeftFromCount,
	resolveDayDenomCounts,
} from "components/Objects/RegisterLedgerCount";

function parseCashLeftFromNote(note) {
	const match = (note || '').match(/Cash Left \$([0-9]+\.?[0-9]*)/);
	return match ? parseFloat(match[1]) : null;
}

function parseCashDepositFromNote(note) {
	const match = (note || '').match(/Deposit \$([0-9]+\.?[0-9]*)/);
	return match ? parseFloat(match[1]) : null;
}

function isSystemLedgerNote(note) {
	const trimmed = (note || '').trim();
	return /^(Start of Day|End of Day|Count)( \(|$)/.test(trimmed)
		|| /\(Total in Register \$/.test(note || '');
}

function isBareLedgerEventNote(notePrefix) {
	return /^(Start of Day|End of Day)$/.test((notePrefix || '').trim());
}

function parseReconFromNote(note) {
	const prefix = note || '';
	return {
		expected_amount: (prefix.match(/Expected \$([0-9]+\.?[0-9]*)/) || [])[1],
		counted_amount: (prefix.match(/Counted \$([0-9]+\.?[0-9]*)/) || [])[1],
		variance: (prefix.match(/Variance \$([0-9]+\.?[0-9]*)/) || [])[1],
		cash_left_in_drawer: parseCashLeftFromNote(prefix),
		cash_deposit: parseCashDepositFromNote(prefix),
	};
}

function coalesceReconValue(dbValue, noteValue) {
	const db = dbValue != null && dbValue !== '' ? parseFloat(dbValue) : null;
	const note = noteValue != null && noteValue !== '' ? parseFloat(noteValue) : null;
	if (db != null && !Number.isNaN(db) && Math.abs(db) >= 0.005) {
		return db;
	}
	if (note != null && !Number.isNaN(note)) {
		return note;
	}
	if (db != null && !Number.isNaN(db)) {
		return db;
	}
	return null;
}

function computeCashDeposit(counted, cashLeft) {
	const countedNum = counted != null && counted !== '' ? parseFloat(counted) : null;
	const cashLeftNum = cashLeft != null && cashLeft !== '' ? parseFloat(cashLeft) : null;
	if (countedNum == null || Number.isNaN(countedNum) || cashLeftNum == null || Number.isNaN(cashLeftNum)) {
		return null;
	}
	const deposit = Math.round((countedNum - cashLeftNum) * 100) / 100;
	return deposit > 0.005 ? deposit : null;
}

function registerSettingsFromProps(props) {
	const hasRetainedFloat = props.retainedFloat != null
		&& props.retainedFloat !== ''
		&& !Number.isNaN(parseFloat(props.retainedFloat));
	return {
		auditDenominations: parseCustomizationBool(props.registerAuditDenominations, false),
		requireVarianceNote: parseCustomizationBool(props.registerRequireVarianceNote, false),
		retainedFloat: hasRetainedFloat ? parseFloat(props.retainedFloat) : null,
	};
}

export default class RegisterLedgerObject extends Framework.Components.Object {
	static propTypes = {
		current_amount: PropTypes.string,
		method: PropTypes.string,
		retainedFloat: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		registerAuditDenominations: PropTypes.oneOfType([PropTypes.bool, PropTypes.number, PropTypes.string]),
		registerRequireVarianceNote: PropTypes.oneOfType([PropTypes.bool, PropTypes.number, PropTypes.string]),
		registerDefaultDenominations: PropTypes.string,
		countResult: PropTypes.object,
		lockedFromCount: PropTypes.bool,
		editMode: PropTypes.bool,
		canEditNote: PropTypes.bool,
	};

	constructor(props) {
		/**
		 *  @type {Registers_Ledgers_Object} this.state
		 */
		super(props, Types.Registers_Ledgers._name);
		const settings = registerSettingsFromProps(props);

		if (!props.editMode) {
			this.state.pkey = null;
			this.update = API.RequestType.CREATE;
		}
		this.state.type = true;
		this.state.amount = "0.00";
		this.state.cash_left_in_drawer = "0.00";
		this.state.denomCounts = emptyDenomCounts();
		this.state.retainedFloat = settings.retainedFloat;
		this.state.auditDenominations = settings.auditDenominations;
		this.state.requireVarianceNote = settings.requireVarianceNote;
		if (!props.editMode) {
			if (props.lockedFromCount && props.countResult && (props.method === 'sod' || props.method === 'eod')) {
				this.applyCountToDayState(props.method, props.countResult);
			} else {
				this.applyMethodToState(props.method);
			}
		}
	}

	componentDidUpdate(prevProps) {
		if (!this.props.editMode) {
			this.update = API.RequestType.CREATE;
		}
		if (!this.props.editMode && (
			prevProps.method !== this.props.method
			|| prevProps.countResult !== this.props.countResult
			|| prevProps.lockedFromCount !== this.props.lockedFromCount
		)) {
			if (this.props.lockedFromCount && this.props.countResult && (this.props.method === 'sod' || this.props.method === 'eod')) {
				this.applyCountToDayState(this.props.method, this.props.countResult, true);
			} else {
				this.applyMethodToState(this.props.method, true);
			}
		}
	}

	applyCountToDayState(method, countResult, useSetState = false) {
		const settings = registerSettingsFromProps(this.props);
		const defaultDenomSpread = this.props.registerDefaultDenominations;
		const cashLeft = resolveCashLeftFromCount(countResult, settings, method, defaultDenomSpread);
		const denomCounts = resolveDayDenomCounts(
			countResult,
			settings,
			method,
			defaultDenomSpread
		);
		const amountBaseline = countResult.counted ?? parseFloat(countResult.counted_amount) ?? 0;
		const amountChanged = Math.round((cashLeft - amountBaseline) * 100) / 100;
		const cashDeposit = method === 'eod'
			? computeCashDeposit(countResult.counted ?? parseFloat(countResult.counted_amount), cashLeft)
			: null;
		const nextState = {
			method,
			event: method === 'eod' ? 'End of Day' : 'Start of Day',
			note: method === 'eod' ? 'End of Day' : 'Start of Day',
			cash_left_in_drawer: formatMoney(cashLeft),
			amount: formatMoney(amountChanged),
			denomCounts,
			denominations: JSON.stringify(buildDenomPayload(denomCounts)),
			counted_amount: countResult.counted_amount,
			countUserNote: countResult.user_note || '',
			user_note: '',
			user_note_error: '',
			countExpected: countResult.expectedBefore,
			countCounted: countResult.counted,
			countVariance: countResult.variance,
			type: true,
			cash_deposit: cashDeposit != null ? formatMoney(cashDeposit) : '',
		};

		if (useSetState) {
			this.setState(nextState);
		} else {
			Object.assign(this.state, nextState);
		}
	}

	loadSelfAfter(data) {
		if (!this.props.editMode) {
			return;
		}
		const {prefix, userNote} = this.parseLedgerNote(data.note || '');
		const parsed = parseReconFromNote(prefix);
		let expected = coalesceReconValue(data.expected_amount, parsed.expected_amount);
		let counted = coalesceReconValue(data.counted_amount, parsed.counted_amount);
		let variance = coalesceReconValue(data.variance, parsed.variance);
		const cashLeft = coalesceReconValue(
			data.cash_left_in_drawer,
			parsed.cash_left_in_drawer != null ? parsed.cash_left_in_drawer : parseCashLeftFromNote(prefix)
		);
		let cashDeposit = coalesceReconValue(data.cash_deposit, parsed.cash_deposit);
		if (cashDeposit == null && data.event === 'End of Day' && counted != null && cashLeft != null) {
			const computed = Math.round((counted - cashLeft) * 100) / 100;
			if (computed > 0.005) {
				cashDeposit = computed;
			}
		}
		const denomCounts = this.parseDenominations(data.denominations);
		if ((counted == null || Math.abs(counted) < 0.005) && Object.keys(data.denominations || {}).length > 0) {
			const fromDenoms = this.computeCounted(denomCounts);
			if (fromDenoms >= 0.005) {
				counted = fromDenoms;
			}
		}
		if (
			(expected == null || Math.abs(expected) < 0.005)
			&& (data.event === 'Start of Day' || data.event === 'End of Day')
		) {
			const cashLeftForInfer = coalesceReconValue(
				data.cash_left_in_drawer,
				parsed.cash_left_in_drawer != null ? parsed.cash_left_in_drawer : parseCashLeftFromNote(prefix)
			);
			const amountVal = parseFloat(data.amount);
			if (cashLeftForInfer != null && !Number.isNaN(amountVal)) {
				const inferred = Math.round((cashLeftForInfer - amountVal) * 100) / 100;
				if (Math.abs(inferred) >= 0.005) {
					expected = inferred;
				}
			}
		}
		if (variance == null && expected != null && counted != null) {
			variance = Math.round((counted - expected) * 100) / 100;
		} else if (
			(variance == null || Math.abs(variance) < 0.005)
			&& expected != null
			&& counted != null
			&& Math.abs(counted - expected) >= 0.005
		) {
			variance = Math.round((counted - expected) * 100) / 100;
		}
		this.setState({
			notePrefix: prefix,
			user_note: userNote,
			denomCounts,
			expected_amount: expected,
			counted_amount: counted,
			variance: variance,
			cash_left_in_drawer: cashLeft != null ? cashLeft.toFixed(2) : '',
			cash_deposit: cashDeposit != null ? cashDeposit.toFixed(2) : '',
			event: data.event,
			amount: data.amount,
			created: data.created,
			method: data.event === 'End of Day' ? 'eod' : data.event === 'Start of Day' ? 'sod' : '',
			pkey: data.pkey,
		});
	}

	parseLedgerNote(note) {
		const sep = ' | ';
		const idx = (note || '').indexOf(sep);
		if (idx >= 0) {
			return {
				prefix: note.substring(0, idx),
				userNote: note.substring(idx + sep.length),
			};
		}
		if (isSystemLedgerNote(note)) {
			return {prefix: note || '', userNote: ''};
		}
		return {prefix: '', userNote: note || ''};
	}

	parseDenominations(json) {
		const counts = emptyDenomCounts();
		if (!json) {
			return counts;
		}
		try {
			const data = typeof json === 'string' ? JSON.parse(json) : json;
			DENOMINATIONS.forEach(d => {
				if (data[d.key] != null && data[d.key] !== '') {
					counts[d.key] = String(data[d.key]);
				}
			});
			return counts;
		} catch (e) {
			return counts;
		}
	}

	defaultCashLeftInDrawer() {
		return '0.00';
	}

	applyMethodToState(method, useSetState = false) {
		const nextState = {method: method || ''};
		const current = parseFloat(this.props.currentAmount) || 0;
		const defaultCashLeft = this.defaultCashLeftInDrawer();

		if (method === "sod") {
			Object.assign(nextState, {
				note: "Start of Day",
				event: "Start of Day",
				cash_left_in_drawer: defaultCashLeft,
				amount: (parseFloat(defaultCashLeft) - current).toFixed(2),
				type: true,
				denomCounts: emptyDenomCounts(),
				denominations: "",
				user_note: "",
			});
		} else if (method === "eod") {
			Object.assign(nextState, {
				note: "End of Day",
				event: "End of Day",
				cash_left_in_drawer: defaultCashLeft,
				amount: (parseFloat(defaultCashLeft) - current).toFixed(2),
				type: true,
				denomCounts: emptyDenomCounts(),
				denominations: "",
				user_note: "",
			});
		} else if (method === "adj") {
			Object.assign(nextState, {
				note: "",
				event: "Adjustment",
				amount: "",
				cash_left_in_drawer: "",
				type: true,
				denomCounts: emptyDenomCounts(),
				denominations: "",
				user_note: "",
			});
		}

		if (useSetState) {
			this.setState(nextState);
		} else {
			Object.assign(this.state, nextState);
		}
	}

	handleTypeChange = (event) => {
		let type = false;
		if (event.currentTarget.id === 'true') type = true
		this.setState({type: type})
	}

	handleAdjAmountUpdate = (name, value, property) => {
		const parsed = parseFloat(value);
		if (!isNaN(parsed) && parsed < 0) {
			value = "0.00";
		}
		this.handleTextFieldPropertyUpdate(name, value, property);
	}

	normalizeCashLeftForSave = (value) => {
		if (value === '' || value == null) {
			return '0.00';
		}
		const parsed = parseFloat(value);
		if (Number.isNaN(parsed) || parsed < 0) {
			return '0.00';
		}
		return parsed.toFixed(2);
	};

	handleCashLeftUpdate = (value, property) => {
		if (value == null) {
			return;
		}
		if (value === '') {
			this.setState({
				cash_left_in_drawer: '',
				cash_left_in_drawer_error: property && property.validate ? property.validate('') : '',
				amount: this.computeAmountChanged(0).toFixed(2),
			});
			return;
		}
		if (!/^\d*\.?\d{0,2}$/.test(value)) {
			return;
		}
		const parsed = parseFloat(value);
		if (!Number.isNaN(parsed) && parsed < 0) {
			value = '0';
		}
		const cashLeft = Number.isNaN(parseFloat(value)) ? 0 : parseFloat(value);
		this.setState({
			cash_left_in_drawer: value,
			cash_left_in_drawer_error: property && property.validate ? property.validate(value) : '',
			amount: this.computeAmountChanged(cashLeft).toFixed(2),
		});
	};

	handleCashLeftChange = (event) => {
		this.handleCashLeftUpdate(event.target.value, this.model.properties.cash_left_in_drawer);
	};

	handleCashLeftBlur = () => {
		const property = this.model.properties.cash_left_in_drawer;
		const next = this.normalizeCashLeftForSave(this.state.cash_left_in_drawer);
		if (this.state.cash_left_in_drawer !== next) {
			const cashLeft = parseFloat(next);
			this.setState({
				cash_left_in_drawer: next,
				cash_left_in_drawer_error: property && property.validate ? property.validate(next) : '',
				amount: this.computeAmountChanged(cashLeft).toFixed(2),
			});
		}
	};

	computeCounted = (counts) => {
		const source = counts || this.state.denomCounts || {};
		let total = 0;
		DENOMINATIONS.forEach(d => {
			const c = parseInt(source[d.key], 10);
			if (!Number.isNaN(c) && c > 0) total += parseFloat(d.key) * c;
		});
		return Math.round(total * 100) / 100;
	}

	handleDenomChange = (key) => (event) => {
		const raw = event.currentTarget.value;
		const counts = {...this.state.denomCounts, [key]: raw === '' ? '0' : raw};
		this.setState({
			denomCounts: counts,
			denominations: JSON.stringify(this.buildDenomPayload(counts)),
		});
	}

	buildDenomPayload = (counts) => {
		const payload = {};
		DENOMINATIONS.forEach(d => {
			const c = parseInt(counts[d.key], 10);
			if (!Number.isNaN(c) && c > 0) payload[d.key] = c;
		});
		return payload;
	}

	getExpected = () => parseFloat(this.props.currentAmount) || 0;

	getAmountChangedBaseline = () => {
		if (this.props.lockedFromCount && this.state.countCounted != null && this.state.countCounted !== '') {
			const parsed = parseFloat(this.state.countCounted);
			if (!Number.isNaN(parsed)) {
				return parsed;
			}
		}
		return this.getExpected();
	};

	hasRetainedFloat = () => {
		const rf = this.state.retainedFloat;
		return rf != null && !Number.isNaN(rf);
	};

	getExpectedDayLabel = (method) => (
		method === 'eod' ? 'End of Day Expected' : 'Start of Day Expected'
	);

	computeAmountChanged = (cashLeftValue) => {
		const baseline = this.getAmountChangedBaseline();
		const cashLeft = cashLeftValue != null
			? cashLeftValue
			: parseFloat(this.state.cash_left_in_drawer);
		const left = Number.isNaN(cashLeft) ? 0 : cashLeft;
		return Math.round((left - baseline) * 100) / 100;
	};

	getEffectiveCounted = () => {
		if (this.state.countCounted != null && this.state.countCounted !== '') {
			const parsed = parseFloat(this.state.countCounted);
			if (!Number.isNaN(parsed)) {
				return parsed;
			}
		}
		return this.computeCounted();
	};

	getCashHelperDisplays = (method) => {
		const cashLeftRaw = this.state.cash_left_in_drawer;
		if (cashLeftRaw === '' || cashLeftRaw == null) {
			return [];
		}
		const cashLeft = parseFloat(cashLeftRaw);
		if (Number.isNaN(cashLeft)) {
			return [];
		}

		const displays = [];

		if (method === 'eod') {
			const counted = this.getEffectiveCounted();
			const deposit = computeCashDeposit(counted, cashLeft);
			if (deposit != null) {
				displays.push({label: 'Cash Deposit', value: deposit});
			}
		}

		if (this.hasRetainedFloat()) {
			const add = Math.round((this.state.retainedFloat - cashLeft) * 100) / 100;
			if (add > 0.005) {
				displays.push({label: 'Amount to Add into Drawer', value: add});
			}
		}

		return displays;
	};

	savePreflightCount = async () => {
		const countResult = this.props.countResult;
		if (!countResult) {
			return true;
		}

		const countModel = new API.BuildClass(Types.Registers_Ledgers._name, API.RequestType.CREATE);
		countModel.parent = this.model.parent || this.props.parent;
		countModel.parent_binary_id = this.model.parent_binary_id;
		const countRequest = {
			state: {
				method: 'count',
				amount: '0.00',
				event: 'Count',
				note: 'Count',
				counted_amount: countResult.counted_amount,
				denominations: this.state.auditDenominations ? (countResult.denominations || '') : '',
				user_note: countResult.user_note || '',
				type: true,
				pkey: null,
				loading: false,
			},
			props: {
				parent: this.props.parent,
			},
			model: countModel,
			handleProcess: () => true,
		};

		try {
			await countModel.submit(countRequest, API.RequestType.CREATE, true, true);
			return true;
		} catch (e) {
			API.Toast.error('Failed to save the count before completing Start/End of Day.', {
				position: API.Toast.POSITION.TOP_RIGHT,
			});
			return false;
		}
	};

	confirmZeroCountedSubmit = (method) => {
		const counted = this.props.lockedFromCount && this.props.countResult
			? parseFloat(this.props.countResult.counted) || 0
			: this.getEffectiveCounted();
		if (counted >= 0.005) {
			return true;
		}
		const label = method === 'eod' ? 'End of Day' : 'Start of Day';
		return window.confirm(`Counted cash is $0.00. Are you sure you want to submit this ${label}?`);
	};

	confirmCashLeftDenomMismatch = (method) => {
		if (!this.state.auditDenominations || (method !== 'sod' && method !== 'eod')) {
			return {proceed: true, userNote: String(this.state.user_note || '').trim()};
		}

		const cashLeft = this.normalizeCashLeftForSave(this.state.cash_left_in_drawer);
		const mismatch = getCashLeftDenomMismatch(cashLeft, this.state.denomCounts);
		if (!mismatch) {
			return {proceed: true, userNote: String(this.state.user_note || '').trim()};
		}

		const label = method === 'eod' ? 'End of Day' : 'Start of Day';
		const confirmed = window.confirm(
			`Cash Left in Drawer ($${mismatch.cashLeft.toFixed(2)}) does not match `
			+ `the denomination total ($${mismatch.denomTotal.toFixed(2)}) for this ${label}.\n\n`
			+ `${mismatch.message}.\n\nContinue anyway?`
		);
		if (!confirmed) {
			return {proceed: false, userNote: String(this.state.user_note || '').trim()};
		}

		return {
			proceed: true,
			userNote: appendCashLeftDenomMismatchNote(this.state.user_note, mismatch),
		};
	};

	renderCashLeftDenomMismatchAlert = () => {
		const method = this.props.method;
		if (!this.state.auditDenominations || (method !== 'sod' && method !== 'eod')) {
			return null;
		}

		const mismatch = getCashLeftDenomMismatch(this.state.cash_left_in_drawer, this.state.denomCounts);
		if (!mismatch) {
			return null;
		}

		return (
			<div className="alert alert-warning" style={{marginTop: 8, marginBottom: 0}}>
				Cash Left in Drawer (${mismatch.cashLeft.toFixed(2)}) does not match the denomination
				total (${mismatch.denomTotal.toFixed(2)}): {mismatch.message}.
			</div>
		);
	};

	handleSubmit = async (ev) => {
		const method = this.props.method;
		if (
			!this.props.editMode
			&& (method === 'sod' || method === 'eod')
			&& !this.confirmZeroCountedSubmit(method)
		) {
			this.setState({loading: false});
			return;
		}

		let sodEodUserNote = String(this.state.user_note || '').trim();
		if (!this.props.editMode && (method === 'sod' || method === 'eod')) {
			const mismatchResult = this.confirmCashLeftDenomMismatch(method);
			if (!mismatchResult.proceed) {
				this.setState({loading: false});
				return;
			}
			sodEodUserNote = mismatchResult.userNote;
		}

		if (this.props.parent && !this.model.parent) {
			this.model.parent = this.props.parent;
		}

		if (this.props.editMode) {
			await new Promise((resolve) => {
				this.setState({
					user_note: String(this.state.user_note || '').trim(),
				}, resolve);
			});
		} else if (method === 'sod' || method === 'eod') {
			const cashLeft = this.normalizeCashLeftForSave(this.state.cash_left_in_drawer);
			const cashLeftNum = parseFloat(cashLeft);
			const countedAmount = this.state.counted_amount || formatMoney(this.state.countCounted || 0);
			const cashDeposit = method === 'eod'
				? computeCashDeposit(this.state.countCounted ?? countedAmount, cashLeft)
				: null;
			await new Promise((resolve) => {
				this.setState({
					method,
					event: method === 'eod' ? 'End of Day' : 'Start of Day',
					note: method === 'eod' ? 'End of Day' : 'Start of Day',
					cash_left_in_drawer: cashLeft,
					amount: this.computeAmountChanged(cashLeftNum).toFixed(2),
					counted_amount: countedAmount,
					cash_deposit: cashDeposit != null ? cashDeposit.toFixed(2) : '',
					denominations: this.state.auditDenominations
						? JSON.stringify(buildDenomPayload(this.state.denomCounts))
						: this.state.denominations,
					user_note: sodEodUserNote,
				}, resolve);
			});

			if (this.props.lockedFromCount && this.props.countResult) {
				const countSaved = await this.savePreflightCount();
				if (!countSaved) {
					this.setState({loading: false});
					return;
				}
			}
		}

		this.update = API.RequestType.CREATE;
		await super.handleSubmit(ev);
	};

	renderEditableAmountField = (label, value, onChange, onBlur, error) => (
		<TextField
			label={label}
			margin={"dense"}
			variant="outlined"
			value={value ?? ''}
			onChange={onChange}
			onBlur={onBlur}
			error={!!error}
			helperText={error || ''}
			fullWidth
			InputProps={{startAdornment: <i className="fa fa-usd" style={{marginRight: 5}}/>}}
		/>
	);

	renderReadOnlyAmountField = (label, value) => (
		<TextField
			label={label}
			margin={"dense"}
			variant="outlined"
			value={Number.isNaN(parseFloat(value)) ? '0.00' : parseFloat(value).toFixed(2)}
			fullWidth
			disabled
			InputProps={{startAdornment: <i className="fa fa-usd" style={{marginRight: 5}}/>}}
		/>
	);

	getVariance = () => {
		if (this.props.editMode && this.state.variance != null && this.state.variance !== '') {
			return Math.round(parseFloat(this.state.variance) * 100) / 100;
		}
		if (this.state.auditDenominations !== true) {
			return 0;
		}
		return Math.round((this.computeCounted() - this.getExpected()) * 100) / 100;
	};

	renderDenominations = (readOnly = false) => {
		const object = this.state;
		const rows = [];
		for (let i = 0; i < DENOMINATIONS.length; i += 6) {
			rows.push(DENOMINATIONS.slice(i, i + 6));
		}
		return (
			<>
				{rows.map((denoms, rowIndex) => (
					<div className={"row"} key={rowIndex}>
						{denoms.map(d => (
							<div className={"col-2"} key={d.key} style={{marginBottom: 10}}>
								<TextField
									type={"number"}
									label={d.label}
									margin={"dense"}
									variant="outlined"
									value={object.denomCounts[d.key] ?? '0'}
									onChange={readOnly ? undefined : this.handleDenomChange(d.key)}
									inputProps={{min: 0}}
									fullWidth={true}
									disabled={readOnly}
								/>
							</div>
						))}
					</div>
				))}
			</>
		);
	}

	renderDayAmountRow = (method) => {
		const object = this.state;
		const cashHelpers = this.getCashHelperDisplays(method);
		const fields = [];

		if (this.hasRetainedFloat()) {
			fields.push({
				key: 'day-expected',
				label: this.getExpectedDayLabel(method),
				value: this.state.retainedFloat,
				readOnly: true,
			});
		}

		fields.push({
			key: 'cash-left',
			label: 'Cash Left in Drawer',
			value: object.cash_left_in_drawer,
			readOnly: false,
			error: object.cash_left_in_drawer_error,
		});

		fields.push({
			key: 'amount-changed',
			label: 'Amount Changed',
			value: this.computeAmountChanged(),
			readOnly: true,
		});

		cashHelpers.forEach((helper) => {
			fields.push({
				key: helper.label,
				label: helper.label,
				value: helper.value,
				readOnly: true,
			});
		});

		return (
			<div className="row" style={{marginTop: 8}}>
				{fields.map((field) => (
					<div className="col-3" key={field.key}>
						{field.readOnly
							? this.renderReadOnlyAmountField(field.label, field.value)
							: this.renderEditableAmountField(
								field.label,
								field.value,
								this.handleCashLeftChange,
								this.handleCashLeftBlur,
								field.error
							)}
					</div>
				))}
			</div>
		);
	};

	renderCashReconciliationFields = (method) => {
		const object = this.state;
		const useDenoms = this.state.auditDenominations === true && !this.props.editMode;
		const counted = this.getEffectiveCounted();
		const variance = this.getVariance();
		const noteRequired = this.state.requireVarianceNote
			&& useDenoms
			&& Math.abs(variance) >= 0.005;

		return (
			<div style={{gap: 10, flexDirection: "column", display: "flex", marginTop: 8}}>
				{useDenoms &&
					<div className={"row"}>
						<div className={"col-3"}>
							{this.renderReadOnlyAmountField('Expected Cash in Drawer', this.getExpected())}
						</div>
						<div className={"col-3"}>
							{this.renderReadOnlyAmountField('Counted Cash in Drawer', counted)}
						</div>
						<div className={"col-3"}>
							{this.renderReadOnlyAmountField('Counted Variance', variance)}
						</div>
					</div>}
				{!useDenoms &&
					<div className={"row"}>
						<div className={"col-3"}>
							{this.renderReadOnlyAmountField('Expected Cash in Drawer', this.getExpected())}
						</div>
					</div>}
				{this.renderDayAmountRow(method)}
				{this.renderCashLeftDenomMismatchAlert()}
				<div className="form-group">
					<Framework.Elements.TextField
						name={"user_note"}
						label={noteRequired ? "Variance Note (required)" : "Note (optional)"}
						property={this.model.properties.user_note}
						value={object.user_note}
						error={object.user_note_error}
						update={this.handleTextFieldPropertyUpdate}
						multiline={true}
					/>
				</div>
			</div>
		);
	};

	renderTextAmountForm = (method) => this.renderCashReconciliationFields(method);

	renderLockedDayForm = (method) => {
		const object = this.state;
		const title = method === 'eod' ? 'End of Day' : 'Start of Day';
		const dayLabel = method === 'eod' ? 'End of Day' : 'Start of Day';

		return (
			<div>
				<h4 className="section-title">{title}</h4>
				<div className="row">
					<div className="col-3">
						{this.renderReadOnlyAmountField('Expected Cash in Drawer', object.countExpected)}
					</div>
					<div className="col-3">
						{this.renderReadOnlyAmountField('Counted Cash in Drawer', object.countCounted)}
					</div>
					<div className="col-3">
						{this.renderReadOnlyAmountField('Counted Variance', object.countVariance)}
					</div>
				</div>
				{this.state.auditDenominations &&
					<>
						<h4 className="section-title" style={{marginTop: 12}}>{title} Register Denominations</h4>
						{this.renderDenominations(false)}
					</>}
				{this.renderDayAmountRow(method)}
				{this.renderCashLeftDenomMismatchAlert()}
				{object.countUserNote ?
					<div className="form-group" style={{marginTop: 8}}>
						<TextField
							label="Count Note"
							value={object.countUserNote}
							margin="dense"
							variant="outlined"
							fullWidth
							disabled
							multiline
						/>
					</div>
					: null}
				<div className="form-group" style={{marginTop: 8}}>
					<Framework.Elements.TextField
						name={"user_note"}
						label={`${dayLabel} Note (optional)`}
						property={this.model.properties.user_note}
						value={object.user_note}
						error={object.user_note_error}
						update={this.handleTextFieldPropertyUpdate}
						multiline={true}
					/>
				</div>
			</div>
		);
	};

	renderEditForm = () => {
		const object = this.state;
		const canEdit = this.props.canEditNote !== false;
		const expected = object.expected_amount;
		const counted = object.counted_amount != null
			? object.counted_amount
			: this.computeCounted();
		const variance = object.variance != null
			? object.variance
			: (expected != null && counted != null
				? Math.round((counted - expected) * 100) / 100
				: null);
		const cashLeft = object.cash_left_in_drawer !== '' && object.cash_left_in_drawer != null
			? parseFloat(object.cash_left_in_drawer)
			: null;
		const cashDepositRaw = object.cash_deposit !== '' && object.cash_deposit != null
			? parseFloat(object.cash_deposit)
			: (object.event === 'End of Day' && counted != null && cashLeft != null
				? Math.max(0, Math.round((counted - cashLeft) * 100) / 100)
				: null);
		const showRecon = expected != null || counted != null || variance != null
			|| Object.keys(object.denomCounts || {}).some(k => parseInt(object.denomCounts[k], 10) > 0);
		const method = object.event === 'End of Day' ? 'eod' : 'sod';
		const secondRowColClass = this.hasRetainedFloat() ? 'col-md-4' : 'col-md-6';

		return (
			<div>
				<div className="row" style={{marginBottom: 8}}>
					<div className="col-md-6">
						<p style={{marginBottom: 4}}><b>Event:</b> {object.event || '-'}</p>
					</div>
					<div className="col-md-6">
						<p style={{marginBottom: 4}}><b>Date:</b> {object.created ? moment(object.created).local().format("MM/DD/YYYY hh:mm A") : '-'}</p>
					</div>
				</div>
				{showRecon &&
					<>
						<div className="row">
							<div className="col-md-4">
								{this.renderReadOnlyAmountField(
									'Expected Cash in Drawer',
									expected != null ? expected : 0
								)}
							</div>
							<div className="col-md-4">
								{this.renderReadOnlyAmountField('Counted Cash in Drawer', counted != null ? counted : 0)}
							</div>
							<div className="col-md-4">
								{this.renderReadOnlyAmountField('Counted Variance', variance != null ? variance : 0)}
							</div>
						</div>
						<div className="row" style={{marginTop: 8}}>
							{this.hasRetainedFloat() &&
								<div className={secondRowColClass}>
									{this.renderReadOnlyAmountField(this.getExpectedDayLabel(method), this.state.retainedFloat)}
								</div>}
							<div className={secondRowColClass}>
								{this.renderReadOnlyAmountField(
									'Cash Left in Drawer',
									cashLeft != null && !Number.isNaN(cashLeft) ? cashLeft : 0
								)}
							</div>
							<div className={secondRowColClass}>
								{this.renderReadOnlyAmountField('Amount Changed', parseFloat(object.amount ?? 0))}
							</div>
						</div>
						{object.event === 'End of Day' && cashDepositRaw != null && !Number.isNaN(cashDepositRaw) && cashDepositRaw > 0.005 ?
							<div className="row" style={{marginTop: 8}}>
								<div className="col-md-4">
									{this.renderReadOnlyAmountField('Cash Deposit', cashDepositRaw)}
								</div>
							</div>
							: null}
					</>}
				{object.notePrefix && !isBareLedgerEventNote(object.notePrefix) ?
					<div className="form-group" style={{marginTop: 12}}>
						<TextField
							label="System Note"
							value={object.notePrefix}
							margin="dense"
							variant="outlined"
							fullWidth
							disabled
							multiline
						/>
					</div>
					: null}
				<h4 className="section-title">Denominations</h4>
				{this.renderDenominations(true)}
				<div className="form-group" style={{marginTop: 8}}>
					{canEdit ?
						<Framework.Elements.TextField
							name={"user_note"}
							label={"Note"}
							property={this.model.properties.user_note}
							value={object.user_note}
							error={object.user_note_error}
							update={this.handleTextFieldPropertyUpdate}
							multiline={true}
						/>
						:
						<TextField
							label={"Note"}
							value={object.user_note || '-'}
							margin="dense"
							variant="outlined"
							fullWidth
							disabled
							multiline
						/>}
				</div>
			</div>
		);
	}

	render() {
		if (this.props.editMode) {
			return (
				<div>
					{this.renderEditForm()}
					<div className={classNames("loader", {'active': this.state.loading})}>
						<ClipLoader color={"#4A4A4A"} loading={this.state.loading}/>
					</div>
				</div>
			);
		}

		/**
		 *  @type {Registers_Ledgers_Object} object
		 */
		let object = this.state;
		/**
		 *  @type {Registers_Ledgers_Properties} properties
		 */
		let properties = this.model.properties

		let method = this.props.method;
		const useDenominations = this.state.auditDenominations === true;
		const lockedDay = this.props.lockedFromCount && this.props.countResult && (method === 'sod' || method === 'eod');

		return (
			<div>
				<form>
					<fieldset disabled={false}>
						<div className={"row"}>
							<div className="col-md">
								{lockedDay && (method === "eod" || method === "sod") ?
									this.renderLockedDayForm(method)
									: null}
								{!lockedDay && method === "adj" ?
									<>
										<div className={"row"}>
											<div className={"col-6"}>
												<div className="form-group">
													<ButtonGroup>
														<Button
															id={'true'}
															className='primary-button-buttongroup'
															color='primary'
															active={this.state.type}
															outline={!this.state.type}
															onClick={this.handleTypeChange}
														>
															Add
														</Button>
														<Button
															id={'false'}
															className='primary-button-buttongroup'
															color='primary'
															active={!this.state.type}
															outline={this.state.type}
															onClick={this.handleTypeChange}
														>
															Remove
														</Button>
													</ButtonGroup>
												</div>
											</div>
											<div className={"col-6"}>
												<div className="form-group">
													<Framework.Elements.DollarAmount
														name={"amount"}
														label={"Amount to Change"}
														value={object.amount}
														property={properties.amount}
														error={object.amount_error}
														update={this.handleAdjAmountUpdate}
														margin={"dense"}
													/>
												</div>
											</div>
										</div>
										<div className="form-group">
											<Framework.Elements.TextField
												name={"note"}
												label={"Reason for adjustment"}
												property={properties.note}
												value={object.note}
												error={object.note_error}
												update={this.handleTextFieldPropertyUpdate}
												multiline={true}
											/>
										</div>
									</>
									: null}
							</div>
						</div>
					</fieldset>
				</form>

				<div className={classNames("loader", {'active': this.state.loading})}>
					<ClipLoader color={"#4A4A4A"} loading={this.state.loading}/>
				</div>
			</div>
		)
	}
}
